/**
 * Create / edit an event (FR-EVENTS-01, AC-EVENT-001).
 *
 * Opens in a dialog over the Event Manager table and the Event Detail page,
 * laid out as a two-column desk form on wide screens. The field list is exactly what `POST /events/v1`
 * accepts. Validation runs twice — inline here before any request, and again on
 * the server, whose `details.field` is mapped back onto the offending control.
 *
 * One rule is client-only: FR-EVENTS-01 makes the opponent REQUIRED for a game
 * while the service accepts a null opponent for any type.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';

import { OverlayDialog } from '../../components/overlay-dialog';
import { TextField } from '../../components/text-field';
import { UnsavedChangesGuard, useFormDirty } from '../../components/unsaved-changes-guard';
import { ApiError } from '../../lib/api-error';
import { EVENT_TYPES, type EventInput, type EventType, type TeamEvent } from '../../lib/api-types';
import {
  EVENT_TYPE_LABELS,
  currentSeasonYear,
  fromDateTimeLocalValue,
  seasonYearOptions,
  toDateTimeLocalValue,
} from '../../lib/event-format';

/** Mirrors `EVENT_NAME_MAX_LENGTH` etc. in services/events/src/constants.ts. */
const NAME_MAX_LENGTH = 160;
const OPPONENT_MAX_LENGTH = 160;
const LOCATION_MAX_LENGTH = 160;
const SEASON_MIN = 1900;
const SEASON_MAX = 2200;

export interface EventDraft {
  name: string;
  eventType: EventType;
  seasonYear: string;
  /** `datetime-local` value in the browser's own time zone. */
  scheduledAt: string;
  scheduledEndAt: string;
  opponentName: string;
  location: string;
}

function emptyDraft(): EventDraft {
  return {
    name: '',
    eventType: 'game',
    seasonYear: String(currentSeasonYear()),
    scheduledAt: '',
    scheduledEndAt: '',
    opponentName: '',
    location: '',
  };
}

function toDraft(event: TeamEvent | null): EventDraft {
  if (event === null) return emptyDraft();
  return {
    name: event.name,
    eventType: event.event_type,
    seasonYear: String(event.season_year),
    scheduledAt: toDateTimeLocalValue(event.scheduled_at),
    scheduledEndAt: toDateTimeLocalValue(event.scheduled_end_at),
    opponentName: event.opponent_name ?? '',
    location: event.location ?? '',
  };
}

/** Field → message keyed by wire field name. Empty means submittable. */
export function validateEventDraft(draft: EventDraft): Record<string, string> {
  const errors: Record<string, string> = {};
  const name = draft.name.trim();
  if (name === '') errors.name = 'Enter an event name.';
  else if (name.length > NAME_MAX_LENGTH) {
    errors.name = `Use at most ${NAME_MAX_LENGTH} characters.`;
  }

  const season = Number(draft.seasonYear);
  if (!Number.isInteger(season) || season < SEASON_MIN || season > SEASON_MAX) {
    errors.season_year = 'Choose a season year.';
  }

  if (draft.scheduledAt.trim() === '') {
    errors.scheduled_at = 'Enter the date and time the event starts.';
  } else if (fromDateTimeLocalValue(draft.scheduledAt) === null) {
    errors.scheduled_at = 'Enter a valid date and time.';
  }

  if (draft.scheduledEndAt.trim() !== '') {
    const start = fromDateTimeLocalValue(draft.scheduledAt);
    const end = fromDateTimeLocalValue(draft.scheduledEndAt);
    if (end === null) errors.scheduled_end_at = 'Enter a valid end date and time.';
    else if (start !== null && end <= start) {
      errors.scheduled_end_at = 'The end time must be after the start time.';
    }
  }

  if (draft.eventType === 'game' && draft.opponentName.trim() === '') {
    errors.opponent_name = 'A game needs an opponent.';
  } else if (draft.opponentName.trim().length > OPPONENT_MAX_LENGTH) {
    errors.opponent_name = `Use at most ${OPPONENT_MAX_LENGTH} characters.`;
  }

  if (draft.location.trim().length > LOCATION_MAX_LENGTH) {
    errors.location = `Use at most ${LOCATION_MAX_LENGTH} characters.`;
  }
  return errors;
}

/** `null` clears an optional column; the service treats `''` the same way. */
export function toEventInput(draft: EventDraft): EventInput {
  const scheduledAt = fromDateTimeLocalValue(draft.scheduledAt);
  return {
    name: draft.name.trim(),
    event_type: draft.eventType,
    season_year: Number(draft.seasonYear),
    // Validation guarantees a parseable start before this runs.
    scheduled_at: scheduledAt ?? new Date().toISOString(),
    scheduled_end_at: fromDateTimeLocalValue(draft.scheduledEndAt),
    opponent_name: draft.opponentName.trim() === '' ? null : draft.opponentName.trim(),
    location: draft.location.trim() === '' ? null : draft.location.trim(),
  };
}

export interface EventFormProps {
  /** `null` → create mode. */
  event: TeamEvent | null;
  /** Performs the POST/PATCH and resolves with the saved row. */
  onSave: (input: EventInput) => Promise<TeamEvent>;
  onSaved: (event: TeamEvent) => void;
  onCancel: () => void;
}

export function EventForm({ event, onSave, onSaved, onCancel }: EventFormProps): JSX.Element {
  const isEditing = event !== null;
  const [draft, setDraft] = useState<EventDraft>(() => toDraft(event));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  // Event metadata typed but not saved must not vanish on a nav click (Task 38).
  const { isDirty, reset: resetDirty } = useFormDirty(formRef);

  useEffect(() => {
    setDraft(toDraft(event));
    setFieldErrors({});
    setFormError(null);
    firstFieldRef.current?.focus();
  }, [event]);

  function update(patch: Partial<EventDraft>): void {
    setDraft((current) => ({ ...current, ...patch }));
  }

  async function submit(): Promise<void> {
    const errors = validateEventDraft(draft);
    setFieldErrors(errors);
    setFormError(null);
    if (Object.keys(errors).length > 0) return;

    setIsSubmitting(true);
    try {
      const saved = await onSave(toEventInput(draft));
      resetDirty();
      onSaved(saved);
    } catch (caught) {
      if (ApiError.isApiError(caught)) {
        setFormError(caught.message);
        if (caught.field !== null) setFieldErrors({ [caught.field]: caught.message });
      } else {
        setFormError('The event could not be saved. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleSubmit(submitEvent: FormEvent<HTMLFormElement>): void {
    submitEvent.preventDefault();
    void submit();
  }

  return (
    <OverlayDialog
      title={isEditing ? `Edit ${event.name}` : 'Create Event'}
      onClose={onCancel}
      panelClassName="event-form-dialog"
    >
      <UnsavedChangesGuard isDirty={isDirty} />

      <form className="stack event-form" ref={formRef} onSubmit={handleSubmit} noValidate>
        {isEditing ? null : (
          <p className="muted event-form__note">
            A new event starts as <strong>Scheduled</strong>. Start it when the team takes the
            field, and close it when the game or practice is over.
          </p>
        )}
        <div className="desk-form desk-form--stacked event-form__grid">
          <div className="desk-form__full event-form__name">
            <TextField
              ref={firstFieldRef}
              label="Event name"
              name="name"
              required
              autoComplete="off"
              value={draft.name}
              onChange={(changeEvent) => update({ name: changeEvent.target.value })}
              error={fieldErrors.name ?? null}
              hint="Example: Week 4 vs. Lincoln High"
            />
          </div>

          <div className={`field${fieldErrors.event_type ? ' field--invalid' : ''}`}>
            <label className="field__label" htmlFor="event-form-type">
              Event type
            </label>
            <select
              id="event-form-type"
              className="field__control"
              value={draft.eventType}
              disabled={isSubmitting}
              onChange={(changeEvent) =>
                update({ eventType: changeEvent.target.value as EventType })
              }
            >
              {EVENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {EVENT_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </div>

          <div className={`field${fieldErrors.season_year ? ' field--invalid' : ''}`}>
            <label className="field__label" htmlFor="event-form-season">
              Season
            </label>
            <select
              id="event-form-season"
              className="field__control"
              value={draft.seasonYear}
              disabled={isSubmitting}
              onChange={(changeEvent) => update({ seasonYear: changeEvent.target.value })}
            >
              {seasonYearOptions().map((year) => (
                <option key={year} value={String(year)}>
                  {year}
                </option>
              ))}
            </select>
            {fieldErrors.season_year ? (
              <span className="field__error" role="alert">
                {fieldErrors.season_year}
              </span>
            ) : null}
          </div>

          <TextField
            label="Starts at"
            name="scheduled_at"
            type="datetime-local"
            required
            value={draft.scheduledAt}
            onChange={(changeEvent) => update({ scheduledAt: changeEvent.target.value })}
            error={fieldErrors.scheduled_at ?? null}
          />
          <TextField
            label="Ends at"
            name="scheduled_end_at"
            type="datetime-local"
            value={draft.scheduledEndAt}
            onChange={(changeEvent) => update({ scheduledEndAt: changeEvent.target.value })}
            error={fieldErrors.scheduled_end_at ?? null}
            hint="Optional. Auto-close counts from this time."
          />
          <TextField
            label="Opponent"
            name="opponent_name"
            autoComplete="off"
            required={draft.eventType === 'game'}
            value={draft.opponentName}
            onChange={(changeEvent) => update({ opponentName: changeEvent.target.value })}
            error={fieldErrors.opponent_name ?? null}
            hint={draft.eventType === 'game' ? 'Required for games.' : 'Optional.'}
          />
          <TextField
            label="Location"
            name="location"
            autoComplete="off"
            value={draft.location}
            onChange={(changeEvent) => update({ location: changeEvent.target.value })}
            error={fieldErrors.location ?? null}
            hint="Optional. Example: Memorial Field"
          />
        </div>

        {formError !== null ? (
          <div className="banner banner--error" role="alert">
            <p>{formError}</p>
          </div>
        ) : null}

        <div className="btn-row dialog-actions">
          <button type="button" className="btn btn--secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Event'}
          </button>
        </div>
      </form>
    </OverlayDialog>
  );
}
